import React from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemText,
  ListItemButton,
  Button,
} from '@mui/material';
import dayjs from 'dayjs';
import LoadingSpinner from '../../../common/LoadingSpinner';
import EmptyState from '../../../common/EmptyState';
import ResultsCount from '../../../common/ResultsCount';
import { ACTION_ITEM_SUMMARY_COLORS } from '../../../../constants/actionItemColors';

// Helper function to get action items summary from _count object
const getActionItemsSummaryFromCount = (counts) => {
  if (!counts) return null;

  const total = counts.actionItems || 0;
  if (total === 0) return null;

  const overdue = counts.overdueActionItems || 0;
  const actionText = `${total} action${total > 1 ? 's' : ''}`;

  if (overdue > 0) {
    return { text: actionText, status: `${overdue} overdue`, color: ACTION_ITEM_SUMMARY_COLORS.OVERDUE };
  }

  // If no overdue, we don't have pending count from backend, so just show total
  return { text: actionText, status: null, color: ACTION_ITEM_SUMMARY_COLORS.DEFAULT };
};

// Helper function to build meeting summary line using _count object
const getMeetingSummaryText = (meeting) => {
  const counts = meeting._count;
  if (!counts) return { parts: [], actionSummary: null };

  const parts = [];

  // Talking points count
  const talkingPointsCount = counts.talkingPoints || 0;
  if (talkingPointsCount > 0) {
    parts.push(`${talkingPointsCount} talking point${talkingPointsCount > 1 ? 's' : ''}`);
  }

  // Action items summary
  const actionSummary = getActionItemsSummaryFromCount(counts);
  if (actionSummary) {
    parts.push(actionSummary);
  }

  return { parts, actionSummary };
};

/**
 * PreviousMeetingsLeftPanel - Left sidebar for Previous Meetings tab showing meetings list
 *
 * @param {Object} props
 * @param {boolean} props.isLoading - Loading state
 * @param {Array} props.previousMeetings - Array of previous meetings
 * @param {Object} props.selectedMeeting - Currently selected meeting
 * @param {Function} props.onSelectMeeting - Callback when a meeting is selected
 * @param {Object} props.pageInfo - Pagination info from API
 * @param {boolean} props.isLoadingMore - Loading state for pagination
 * @param {Function} props.onLoadMore - Callback to load more meetings
 */
const PreviousMeetingsLeftPanel = ({
  isLoading,
  previousMeetings,
  selectedMeeting,
  onSelectMeeting,
  pageInfo,
  isLoadingMore,
  onLoadMore,
}) => {
  return (
    <Paper
      sx={{
        width: '22%',
        minWidth: 220,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid #E2E8F0' }}>
        <ResultsCount
          count={pageInfo?.totalCount ?? previousMeetings.length}
          singular="previous meeting"
          plural="previous meetings"
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
          Meetings with the same participants
        </Typography>
      </Box>

      {isLoading ? (
        <LoadingSpinner />
      ) : previousMeetings.length === 0 ? (
        <Box sx={{ m: 2 }}>
          <EmptyState
            title="No previous meetings found"
            description="No previous meetings found with the same participants"
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <List sx={{ p: 0 }}>
            {previousMeetings.map((m) => (
              <ListItemButton
                key={m.id}
                selected={selectedMeeting?.id === m.id}
                onClick={() => onSelectMeeting(m)}
                sx={{
                  borderBottom: '1px solid #F1F5F9',
                  py: 1.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(37, 99, 235, 0.05)',
                  },
                  '&.Mui-selected .MuiTypography-root[data-title="true"]': {
                    fontWeight: 600,
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" data-title="true" fontWeight={500} noWrap sx={{ mb: 0.5 }}>
                      {m.subject}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">
                        {m.scheduledAt
                          ? dayjs(m.scheduledAt).format('DD/MM/YYYY')
                          : 'Not scheduled'}
                      </Typography>
                      {(() => {
                        const { parts, actionSummary } = getMeetingSummaryText(m);
                        if (parts.length === 0) return null;

                        const talkingPointsCount = m._count?.talkingPoints || 0;

                        return (
                          <Typography variant="caption" component="span" sx={{ fontSize: '11px' }}>
                            {talkingPointsCount > 0 && (
                              <span style={{ color: '#6B7280' }}>
                                {talkingPointsCount} talking point{talkingPointsCount > 1 ? 's' : ''}
                              </span>
                            )}
                            {talkingPointsCount > 0 && actionSummary && (
                              <span style={{ color: '#9CA3AF' }}> · </span>
                            )}
                            {actionSummary && (
                              <>
                                <span style={{ color: '#6B7280' }}>{actionSummary.text}</span>
                                {actionSummary.status && (
                                  <>
                                    <span style={{ color: '#9CA3AF' }}> · </span>
                                    <span style={{ color: actionSummary.color }}>{actionSummary.status}</span>
                                  </>
                                )}
                              </>
                            )}
                          </Typography>
                        );
                      })()}
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
          {pageInfo?.hasNextPage && (
            <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid #E2E8F0' }}>
              <Button
                size="small"
                onClick={onLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? 'Loading...' : 'Load more'}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default PreviousMeetingsLeftPanel;
